import { cn } from '@/lib/utils.ts'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import useGlobalStore from '@/useGlobalStore.ts'
import { ItemsSheet } from '@/components/items-sheet.tsx'
import { Sheet } from '@/components/ui/sheet.tsx'
import api from '@/api.ts'
import { toast } from 'sonner'
import { LibraryCollection } from '@/models.ts'
import dayjs from 'dayjs'
import { Button } from '@/components/ui/button.tsx'
import { ArrowLeft, ArrowRight, Plus, Settings } from 'lucide-react'
import { dispatch, useBus } from '@/useBus.ts'
import useCollectionDetailsModalStore from '@/useCollectionDetailsModalStore.ts'
import { Skeleton } from '@/components/ui/skeleton.tsx'

const PageCollections = ({ className }: { className: string }) => {
	const { t } = useTranslation()
	const { setMode, setData, showModal, setOnSuccessfulFinalAction } = useCollectionDetailsModalStore()
	const [showAllCollectionItemsSheet, setShowAllCollectionItemsSheet] = useState(false)
	const {
		libraryCollectionsList,
		setLibraryCollectionsList,
		availableAppLanguages,
		appLanguageCode,
		loadLibraryCollectionsList,
		isLibraryCollectionsListLoading,
	} = useGlobalStore()
	const [currentCollection, setCurrentCollection] = useState<LibraryCollection | null>(null)

	const openAllCollectionItemsSheet = async (id: number) => {
		setShowAllCollectionItemsSheet(true)
		await loadCollection(id)
	}

	const loadCollection = async (id: number) => {
		api(`/v2/library/collections/${id}`)
			.then((data: LibraryCollection) => {
				setCurrentCollection(data)
				const updatedCollections = libraryCollectionsList.map((col) => (col.id === data.id ? data : col))
				setLibraryCollectionsList(updatedCollections)
			})
			.catch((error) => {
				toast(error.data?.message ?? 'Operation failed')
			})
	}

	const openDialogForNewCollection = () => {
		setOnSuccessfulFinalAction(async () => {
			await loadLibraryCollectionsList()
		})
		setMode('add')
		showModal()
	}

	const openDialogForEditCollection = () => {
		if (currentCollection != null) {
			setOnSuccessfulFinalAction(async () => {
				setShowAllCollectionItemsSheet(false)
				await loadLibraryCollectionsList()
			})
			setData(currentCollection)
			setMode('saved')
			showModal()
		}
	}

	useBus(
		'reload-open-collection',
		async ({ collectionId }) => {
			if (currentCollection?.id === collectionId) {
				await loadCollection(collectionId)
			}
		},
		[currentCollection],
	)

	return (
		<div className={cn(className)}>
			<div className="flex flex-row">
				<Button
					onClick={() => dispatch('change-page', { page: 3 })}
					variant="ghost"
					className="flex items-center justify-start rounded-none p-2 w-full">
					{availableAppLanguages[appLanguageCode].dir === 'rtl' ? (
						<ArrowRight className="h-5 w-5" />
					) : (
						<ArrowLeft className="h-5 w-5" />
					)}
					<h6 className="m-2 text-sm">{t('Back')}</h6>
				</Button>
				<Button onClick={openDialogForNewCollection} variant="ghost" className="p-2">
					<h6 className="m-2 text-sm">{t('Add')}</h6>
					<Plus className="h-5 w-5" />
				</Button>
			</div>
			<div className="masonry-app">
				<Sheet open={showAllCollectionItemsSheet} onOpenChange={setShowAllCollectionItemsSheet}>
					<div className="masonry" role="list">
						{isLibraryCollectionsListLoading && libraryCollectionsList.length <= 0
							? [1, 2, 3, 4].map((i) => (
									<div key={i} className="flex justify-between items-center">
										<Skeleton className="flex items-center justify-center m-2 w-full h-32" />
									</div>
								))
							: libraryCollectionsList.map((item) => (
									<article
										onClick={() => openAllCollectionItemsSheet(item.id)}
										key={item.id}
										className="card min-h-[8rem] justify-between cursor-pointer select-none"
										style={{ background: item.color }}
										role="listitem">
										<div className="text-2xl font-bold mb-4">{item.name}</div>
										<div className="flex flex-col">
											<span className="text-base ms-2">
												{t('Vocabulary')}: {item.vocabularies_count}
											</span>
											<span className="text-base ms-2">
												{t('Chats')}: {item.chats_count}
											</span>
											<span className="text-end text-xs">
												{dayjs().to(dayjs.utc(item.created_at).local())}
											</span>
										</div>
									</article>
								))}
					</div>
					<ItemsSheet
						onClose={() => setShowAllCollectionItemsSheet(false)}
						chatsList={currentCollection?.chats}
						vocabularyList={currentCollection?.vocabularies ?? null}
						sideActionEnd={
							<Button
								onClick={openDialogForEditCollection}
								variant="ghost"
								className="h-7 absolute right-0">
								<Settings className="h-4 w-4" />
							</Button>
						}
					/>
				</Sheet>
			</div>
		</div>
	)
}

export default PageCollections
